"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const CitiesContext = createContext();
export const useCities = () => useContext(CitiesContext);

export const CitiesProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const mountedRef = useRef(true);

  const CITIES_ENDPOINT = "/cities";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetCities = (value) => {
    if (mountedRef.current) setCities(value);
  };

  /* ---------------- FETCH ---------------- */
 const fetchCities = useCallback(async (mandal_id = "") => {
  if (!token) return;
  setCitiesLoading(true);
  try {
    let url = `${baseURL}${CITIES_ENDPOINT}`;
    if (mandal_id) url += `?mandal_id=${mandal_id}`;  // ← ADD THIS

    const res = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload?.message || "Fetch failed");
    safeSetCities(payload?.data || []);
    return payload;
  } catch (err) {
    console.error("[Cities] fetch error:", err);
    if (err.message?.toLowerCase().includes("token")) logout();
    safeSetCities([]);
  } finally {
    if (mountedRef.current) setCitiesLoading(false);
  }
}, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addCity = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${CITIES_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchCities();
        return payload;
      } catch (err) {
        console.error("[Cities] add error:", err);
        throw err;
      }
    },
    [token, adminId, fetchCities]
  );

  /* ---------------- UPDATE ---------------- */
  const updateCity = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${CITIES_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchCities();
        return payload;
      } catch (err) {
        console.error("[Cities] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchCities]
  );

  /* ---------------- DELETE ---------------- */
  const deleteCity = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${CITIES_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetCities((prev) =>
          prev.filter((c) => String(c.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Cities] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateCityStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(
          `${baseURL}${CITIES_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        safeSetCities((prev) =>
          prev.map((c) =>
            String(c.id) === String(id)
              ? { ...c, status }
              : c
          )
        );

        return payload;
      } catch (err) {
        console.error("[Cities] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchCities().catch(() => {});
  }, [isReady, token, fetchCities]);

  return (
    <CitiesContext.Provider
      value={{
        cities,
        citiesLoading,
        fetchCities,
        addCity,
        updateCity,
        deleteCity,
        updateCityStatus,
      }}
    >
      {children}
    </CitiesContext.Provider>
  );
};